import {Dropdown, DropdownItem, Parameters} from 'platform/components';
import {Hide, HStack, Show, ThemeIconKey, Link, Heading} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {always, isNotNil, isNotEmpty, map, pipe, reject, isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  ServiceCaseIntegration,
  ServiceCaseIntegrationAction,
  ServiceCaseIntegrationActionKey,
  ServiceCaseIntegrationCode,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {downloadFile} from '@dms/shared';

import {
  EMPTY_PLACEHOLDER,
  TestIdProps,
  parseDate,
  suffixTestId,
  useBoolean,
  Nullish,
} from 'shared/web';

import {CLARA_VEHICLE_INFO_VIEW} from '../../../pages/ServiceCaseDetail/constants/claraVehicleInfoView';
import {DAIMLER_CLARA_VEHINFO} from '../../../pages/ServiceCaseDetail/constants/daimlerClaraVehinfo';
import {DAIMLER_XENTRY_LINK} from '../../../pages/ServiceCaseDetail/constants/daimlerXentryLink';
import {DAIMLER_XENTRY_SYNC} from '../../../pages/ServiceCaseDetail/constants/daimlerXentrySync';
import {MERCEDES_EVA_CLAIM} from '../../../pages/ServiceCaseDetail/constants/mercedesEvaClaim';
import {MERCEDES_EVA_CLAIM_ACTION} from '../../../pages/ServiceCaseDetail/constants/mercedesEvaClaimAction';
import {MERCEDES_XENTRY_LINK} from '../../../pages/ServiceCaseDetail/constants/mercedesXentryLink';
import {MERCEDES_XENTRY_SYNC} from '../../../pages/ServiceCaseDetail/constants/mercedesXentrySync';
import {MERCEDES_XENTRY_SYNC_ACTION} from '../../../pages/ServiceCaseDetail/constants/mercedesXentrySyncAction';
import {IntegrationCard, IntegrationCardAction} from '../../IntegrationCard/IntegrationCard';

interface IntegrationItemProps extends TestIdProps {
  integration: ServiceCaseIntegration;
  isActionPending: (key: string) => boolean;
  onActionClick: (actionKey?: ServiceCaseIntegrationActionKey | Nullish) => void;
  isExternalActionPending?: boolean;
}

export function IntegrationItem(props: IntegrationItemProps) {
  const formatDateTime = useDateTimeFormatter();
  const [isDownloadDropdownOpen, openDownloadDropdown, closeDownloadDropdown] = useBoolean();

  const isHidden =
    props.integration?.integration?.featureCode === MERCEDES_XENTRY_LINK ||
    props.integration?.integration?.featureCode === DAIMLER_XENTRY_LINK;

  if (isHidden) {
    return null;
  }

  const hasAnyDownloadLinks = isNotNilOrEmpty(props.integration?.downloads);
  const isInactive =
    isNil(props.integration?.integration?.createdAt) &&
    isNil(props.integration?.integration?.updatedAt);

  const actions = pipe(
    reject<ServiceCaseIntegrationAction>((action) => action?.style === 'hidden'),
    map<ServiceCaseIntegrationAction, IntegrationCardAction>(
      (action: ServiceCaseIntegrationAction) => ({
        key: action?.key,
        title: getActionTitle(action?.key),
        icon: getActionIcon(action?.key),
        isLoading: props.isActionPending(`service-case/integration/${action?.key}`),
        isDisabled: action?.style === 'disabled' || props.isExternalActionPending,
        onClick: (e) => {
          e.stopPropagation();
          props.onActionClick(action?.key);
        },
      })
    )
  )(props.integration?.action ?? []);

  const isXentrySync =
    props.integration?.integration?.featureCode === MERCEDES_XENTRY_SYNC ||
    props.integration?.integration?.featureCode === DAIMLER_XENTRY_SYNC;

  const isEvaClaim = props.integration?.integration?.featureCode === MERCEDES_EVA_CLAIM;

  return (
    <Hide key={props.integration?.integration?.featureCode} when={isHidden}>
      <IntegrationCard
        title={
          <Heading size={4}>
            {getIntegrationTitle(props.integration?.integration?.featureCode)}
          </Heading>
        }
        actions={actions}
        notification={{
          level: props.integration?.notification?.level,
          content: props.integration?.notification?.content,
        }}
        isInactive={isInactive}
        data-testid={suffixTestId('card', props)}
      >
        <Show when={isXentrySync}>
          <HStack align="center">
            <Parameters
              color="secondary"
              size="xSmall"
              parameters={[
                i18n.t('entity.orderIntegration.labels.lastSync', {
                  sync:
                    isNotNil(props.integration?.integration?.updatedAt) &&
                    isNotEmpty(props.integration?.integration?.updatedAt)
                      ? formatDateTime(
                          'dateTimeShort',
                          parseDate(props.integration?.integration?.updatedAt)
                        )
                      : i18n.t('entity.orderIntegration.labels.noSync'),
                }),
              ]}
            />
          </HStack>
        </Show>
        <Show
          when={
            isEvaClaim &&
            hasAnyDownloadLinks &&
            isNotNilOrEmpty(props.integration?.integration?.createdAt)
          }
        >
          <HStack align="center" justify="space-between">
            <Parameters
              color="secondary"
              size="xSmall"
              parameters={[
                isNotNil(props.integration?.integration?.createdAt) &&
                isNotEmpty(props.integration?.integration?.createdAt)
                  ? formatDateTime(
                      'dateTimeShort',
                      parseDate(props.integration?.integration?.createdAt)
                    )
                  : EMPTY_PLACEHOLDER,
              ]}
            />
            <Show when={hasAnyDownloadLinks}>
              <Dropdown
                isOpen={isDownloadDropdownOpen}
                onOpen={openDownloadDropdown}
                onClose={closeDownloadDropdown}
                placement="bottom-end"
                dropdownControl={
                  <Link
                    size="small"
                    title={i18n.t('entity.orderIntegration.labels.download', {
                      count: props.integration?.downloads?.length,
                    })}
                  />
                }
              >
                {props.integration?.downloads?.map((download) => (
                  <DropdownItem
                    key={`integration-download-${download.filename}`}
                    label={download.filename}
                    onClick={() => {
                      downloadFile(download.link, {fileName: download.filename});
                    }}
                  />
                ))}
              </Dropdown>
            </Show>
          </HStack>
        </Show>
      </IntegrationCard>
    </Hide>
  );
}

const getActionIcon = (key?: ServiceCaseIntegrationActionKey | Nullish) =>
  match<ServiceCaseIntegrationActionKey | Nullish, ThemeIconKey | undefined>(key)
    .with(CLARA_VEHICLE_INFO_VIEW, always('action/visibility'))
    .with(MERCEDES_XENTRY_SYNC_ACTION, always('action/autorenew'))
    .with(MERCEDES_EVA_CLAIM_ACTION, always('content/add_circle'))
    .otherwise(always(undefined));

const getActionTitle = (key?: ServiceCaseIntegrationActionKey | Nullish) =>
  match<ServiceCaseIntegrationActionKey | Nullish, string>(key)
    .with(CLARA_VEHICLE_INFO_VIEW, always(i18n.t('general.actions.view')))
    .with(MERCEDES_XENTRY_SYNC_ACTION, always(i18n.t('entity.orderIntegration.actions.sync')))
    .with(MERCEDES_EVA_CLAIM_ACTION, always(i18n.t('general.actions.create')))
    .otherwise(always(''));

const getIntegrationTitle = (key?: ServiceCaseIntegrationCode | Nullish) =>
  match<ServiceCaseIntegrationCode | Nullish, string>(key)
    .with(undefined, null, MERCEDES_XENTRY_LINK, DAIMLER_XENTRY_LINK, always(''))
    .with(DAIMLER_CLARA_VEHINFO, always(i18n.t('entity.integration.labels.claraVehicleInfo')))
    .with(
      MERCEDES_XENTRY_SYNC,
      DAIMLER_XENTRY_SYNC,
      always(i18n.t('entity.orderIntegration.labels.xentryOrder'))
    )
    .with(MERCEDES_EVA_CLAIM, always(i18n.t('entity.integration.labels.mercedesEvaClaim')))
    .exhaustive();
